import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/auth/AuthContext'
import { RequireAuth } from '@/auth/RequireAuth'
import { AppShell } from '@/components/AppShell'
import type { NavItem } from '@/components/AppShell'
import { LoginPage } from '@/routes/LoginPage'
import { FieldHome } from '@/routes/field/FieldHome'
import { NewSubmission } from '@/routes/field/NewSubmission'
import { PerDiemOnly } from '@/routes/field/PerDiemOnly'
import { ReviewPage } from '@/routes/ReviewPage'
import { SubmissionDetailPage } from '@/routes/SubmissionDetailPage'
import { OfficeHome } from '@/routes/office/OfficeHome'
import { EmployeesPage } from '@/routes/office/EmployeesPage'
import { CrewsPage } from '@/routes/office/CrewsPage'
import { WorkTypesPage } from '@/routes/office/WorkTypesPage'
import { RegisterPage } from '@/routes/office/RegisterPage'
import { JobDetailPage } from '@/routes/office/JobDetailPage'
import { CrewWeekPage } from '@/routes/CrewWeekPage'
import { JobsPage } from '@/routes/office/JobsPage'
import { JobAssignmentsPage } from '@/routes/office/JobAssignmentsPage'
import { ProvisionalPeoplePage } from '@/routes/office/ProvisionalPeoplePage'
import { JobLocationsPage } from '@/routes/office/JobLocationsPage'
import { ProblemsPage } from '@/routes/office/ProblemsPage'
import { PerDiemPage } from '@/routes/office/PerDiemPage'
import { PerDiemRegisterPage } from '@/routes/office/PerDiemRegisterPage'
import { OvertimeRulesPage } from '@/routes/office/OvertimeRulesPage'
import { WageRatesPage } from '@/routes/office/WageRatesPage'
import { CertificationsPage } from '@/routes/office/CertificationsPage'
import { EmployeeCertsPage } from '@/routes/office/EmployeeCertsPage'
import { CertSetupPage } from '@/routes/office/CertSetupPage'
import { CertNotificationsPage } from '@/routes/office/CertNotificationsPage'
import { TechnicianLanding } from '@/components/FieldShell'
import { CertsHome, CertsShell } from '@/components/CertsShell'
import { MyCerts } from '@/routes/field/MyCerts'
import { CrewCerts } from '@/routes/field/CrewCerts'
import { HelpCenter } from '@/routes/HelpCenter'
import { ChangesPage } from '@/routes/office/ChangesPage'
import { SupervisorCutoffPage } from '@/routes/office/SupervisorCutoffPage'
import { SendToSagePage } from '@/routes/office/SendToSagePage'
import { SageBridgePage } from '@/routes/office/SageBridgePage'

/**
 * One codebase, several role-gated route trees, grouped into modules.
 *
 *   Job Management
 *     /field/*   supervisor PWA — offline-capable, camera, GPS
 *     /office/*  accounting and admin — online only
 *   Certifications
 *     /certs/*   its own menu; behind the server's preview gate
 *
 * They share auth, the API client, the type definitions, and the design
 * system, and differ only in route tree and density. Two separate apps would
 * mean two builds, two auth flows, and two sets of types drifting apart, for
 * no benefit — both talk to the same API about the same domain objects.
 */
function RootRedirect() {
  const { isAuthenticated, hasRole } = useAuth()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <Navigate to={hasRole('Office') ? '/office' : '/field'} replace />
}

/**
 * Help belongs to no module. It wears the chrome of where the person usually
 * works -- the office density for Office and Admin, the field one otherwise --
 * with no menu bar, so the module switch and Back are the way out.
 */
function HelpShell() {
  const { hasRole } = useAuth()
  return (
    <AppShell
      variant={hasRole('Office') ? 'office' : 'field'}
      module={hasRole('Supervisor') ? 'jobs' : 'certs'}
      nav={[]}
    />
  )
}

/** Old certification URLs (/office/certs/...) keep working. */
function OldCertsRedirect() {
  const { pathname, search } = useLocation()
  return <Navigate to={pathname.replace(/^\/office\/certs/, '/certs') + search} replace />
}

/**
 * The office bar, grouped. Sixteen buttons in one row had stopped fitting;
 * related screens now share a dropdown. Problems stays on its own so its
 * badge is always in view.
 */
const OFFICE_NAV: NavItem[] = [
  {
    label: 'Time',
    items: [
      { to: '/office', label: 'Submissions' },
      { to: '/office/register', label: 'Labor register' },
      { to: '/office/review', label: 'Review by week' },
      { to: '/office/crew-week', label: 'Crew by week' },
      { to: '/office/changes', label: 'Changes by week' },
    ],
  },
  {
    label: 'Per diem',
    items: [
      { to: '/office/per-diem-register', label: 'Per diem register' },
      { to: '/office/per-diem', label: 'Per diem rates' },
    ],
  },
  {
    label: 'Pay setup',
    items: [
      { to: '/office/work-types', label: 'Work types' },
      { to: '/office/wage-rates', label: 'Wage rates' },
      { to: '/office/overtime-rules', label: 'Overtime rules' },
      { to: '/office/supervisor-cutoff', label: 'Supervisor cutoff' },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/office/employees', label: 'Employees' },
      { to: '/office/new-people', label: 'New people' },
      { to: '/office/crews', label: 'Crews' },
    ],
  },
  {
    label: 'Jobs',
    items: [
      { to: '/office/jobs', label: 'Jobs' },
      { to: '/office/job-assignments', label: 'Job assignments' },
      { to: '/office/job-locations', label: 'Job locations' },
    ],
  },
  // Everything to do with getting time into Sage, in one place (4 Nov 2026).
  // Its badge counts refused lines nobody has sent again.
  {
    label: 'Sage',
    items: [
      { to: '/office/send-to-sage', label: 'Send to Sage' },
      { to: '/office/sage-bridge', label: 'Bridge status' },
    ],
  },
  { to: '/office/problems', label: 'Problems' },
]

/** The supervisor's phone bar: five buttons fit, and a dropdown is worse with
 *  gloves on, so it stays flat. */
const FIELD_NAV: NavItem[] = [
  { to: '/field', label: 'Today' },
  { to: '/field/new', label: 'New crew day' },
  { to: '/field/per-diem', label: 'Per diem' },
  { to: '/field/review', label: 'My time' },
  { to: '/field/crew-week', label: 'My crew' },
]

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Job Management — field, supervisors and above */}
        <Route
          path="/field"
          element={
            // A technician lands here after sign-in. With certifications on
            // for them, TechnicianLanding sends them to their own; otherwise
            // they see the same "not available" card as always. /field stays
            // Supervisor and up -- it is NOT widened.
            <RequireAuth role="Supervisor" fallback={<TechnicianLanding />}>
              <AppShell variant="field" module="jobs" nav={FIELD_NAV} />
            </RequireAuth>
          }
        >
          <Route index element={<FieldHome />} />
          <Route path="new" element={<NewSubmission />} />
          <Route path="per-diem" element={<PerDiemOnly />} />
          <Route path="review" element={<ReviewPage mine />} />
          <Route path="crew-week" element={<CrewWeekPage />} />
          <Route path="submissions/:submissionId" element={<SubmissionDetailPage />} />
          {/* Moved to the Certifications module. */}
          <Route path="crew-certs" element={<Navigate to="/certs/crew" replace />} />
          <Route path="my-certs" element={<Navigate to="/certs/mine" replace />} />
        </Route>

        {/* Job Management — office, accounting and admin */}
        <Route
          path="/office"
          element={
            <RequireAuth role="Office">
              <AppShell variant="office" module="jobs" nav={OFFICE_NAV} />
            </RequireAuth>
          }
        >
          <Route index element={<OfficeHome />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="review" element={<ReviewPage />} />
          <Route path="crew-week" element={<CrewWeekPage />} />
          <Route path="changes" element={<ChangesPage />} />
          <Route path="supervisor-cutoff" element={<SupervisorCutoffPage />} />
          <Route path="send-to-sage" element={<SendToSagePage />} />
          <Route path="sage-bridge" element={<SageBridgePage />} />
          <Route path="submissions/:submissionId" element={<SubmissionDetailPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="crews" element={<CrewsPage />} />
          <Route path="work-types" element={<WorkTypesPage />} />
          <Route path="wage-rates" element={<WageRatesPage />} />
          {/* The register is the daily-use screen; the rate sheet it resolves
              against is maintenance, and keeps its original path so existing
              links still work. */}
          <Route path="per-diem-register" element={<PerDiemRegisterPage />} />
          <Route path="per-diem" element={<PerDiemPage />} />
          <Route path="overtime-rules" element={<OvertimeRulesPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="job-assignments" element={<JobAssignmentsPage />} />
          <Route path="new-people" element={<ProvisionalPeoplePage />} />
          <Route path="problems" element={<ProblemsPage />} />
          <Route path="jobs/:jobNo" element={<JobDetailPage />} />
          <Route path="job-locations" element={<JobLocationsPage />} />
          <Route path="certs/*" element={<OldCertsRedirect />} />
        </Route>

        {/* Certifications — its own module and menu. Everyone signed in passes
            the guard; CertsShell shows "not switched on" outside the server's
            preview gate, and the API answers 404 there regardless. Which menu
            items appear comes from the user's capabilities. */}
        <Route
          path="/certs"
          element={
            <RequireAuth role="Technician">
              <CertsShell />
            </RequireAuth>
          }
        >
          <Route index element={<CertsHome matrix={<CertificationsPage />} />} />
          <Route path="people/:employeeId" element={<EmployeeCertsPage />} />
          <Route path="requirements" element={<CertSetupPage tabs={['requirements']} />} />
          <Route path="types" element={<CertSetupPage tabs={['types']} />} />
          <Route path="setup" element={<CertSetupPage tabs={['categories', 'relations']} />} />
          <Route path="notifications" element={<CertNotificationsPage />} />
          <Route path="crew" element={<CrewCerts />} />
          <Route path="mine" element={<MyCerts />} />
        </Route>
        <Route path="/me" element={<Navigate to="/certs/mine" replace />} />

        {/* Help -- everyone signed in. */}
        <Route
          path="/help"
          element={
            <RequireAuth role="Technician">
              <HelpShell />
            </RequireAuth>
          }
        >
          <Route index element={<HelpCenter />} />
          <Route path=":slug" element={<HelpCenter />} />
        </Route>

        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}
